// frontend/src/components/admin/BulkOperations.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { 
  Users, 
  Building, 
  RotateCcw, 
  AlertTriangle, 
  CheckCircle,
  Upload,
  Download,
  RefreshCw
} from 'lucide-react';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';

/**
 * BulkOperations Component
 * Perform bulk operations on preferences (reset, import/export)
 */
const BulkOperations = ({ userRole }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [availablePreferences, setAvailablePreferences] = useState([]);
  const [activeOperation, setActiveOperation] = useState('reset-users');
  const [importData, setImportData] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const entityId = localStorage.getItem('entityId');
      
      // Fetch users, entities, and preferences in parallel
      const [usersResponse, entitiesResponse, preferencesResponse] = await Promise.all([
        axios.get(`/api/admin/preferences/${entityId}/users`), // Get all users
        axios.get(`/api/admin/preferences/${entityId}/entities`), // Get all entities
        axios.get(`/api/admin/preferences/${entityId}/definitions`) // Get preference definitions
      ]);

      if (usersResponse.data.success) {
        setUsers(usersResponse.data.data);
      }
      
      if (entitiesResponse.data.success) {
        setEntities(entitiesResponse.data.data);
      }
      
      if (preferencesResponse.data.success) {
        setAvailablePreferences(preferencesResponse.data.data);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errorFetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetUsers = () => {
    if (selectedUsers.length === 0) {
      toast.error(t('pleaseSelectUsers'));
      return;
    }

    const confirmMessage = selectedPreferences.length > 0 
      ? t('confirmResetSpecificUserPreferences', { 
          userCount: selectedUsers.length,
          prefCount: selectedPreferences.length 
        })
      : t('confirmResetAllUserPreferences', { userCount: selectedUsers.length });

    setPendingConfirmation({
      type: 'reset-users',
      title: t('resetUserPreferences'),
      confirmLabel: t('resetUserPreferences'),
      message: confirmMessage,
      payload: {
        user_ids: [...selectedUsers],
        reset_all: selectedPreferences.length === 0,
        preference_keys: selectedPreferences.length > 0 ? [...selectedPreferences] : undefined
      }
    });
  };

  const confirmPendingOperation = async () => {
    if (!pendingConfirmation) return;

    try {
      setLoading(true);
      const entityId = localStorage.getItem('entityId');
      const endpoint = pendingConfirmation.type === 'reset-users' ? 'reset-users' : 'reset-entities';
      const response = await axios.post(
        `/api/admin/preferences/${entityId}/${endpoint}`,
        pendingConfirmation.payload
      );
      
      if (response.data.success) {
        if (pendingConfirmation.type === 'reset-users') {
          toast.success(t('userPreferencesResetSuccess', {
            userCount: response.data.affected_users,
            prefCount: response.data.affected_preferences
          }));
          setSelectedUsers([]);
        } else {
          toast.success(t('entitySettingsResetSuccess', {
            entityCount: response.data.affected_entities,
            settingCount: response.data.affected_settings
          }));
          setSelectedEntities([]);
        }
        setSelectedPreferences([]);
        setPendingConfirmation(null);
      }
      
    } catch (error) {
      console.error('Error performing bulk reset:', error);
      toast.error(
        error.response?.data?.error ||
        (pendingConfirmation.type === 'reset-users'
          ? t('errorResettingUserPreferences')
          : t('errorResettingEntitySettings'))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetEntities = () => {
    if (selectedEntities.length === 0) {
      toast.error(t('pleaseSelectEntities'));
      return;
    }

    const confirmMessage = selectedPreferences.length > 0 
      ? t('confirmResetSpecificEntitySettings', { 
          entityCount: selectedEntities.length,
          settingCount: selectedPreferences.length 
        })
      : t('confirmResetAllEntitySettings', { entityCount: selectedEntities.length });

    setPendingConfirmation({
      type: 'reset-entities',
      title: t('resetEntitySettings'),
      confirmLabel: t('resetEntitySettings'),
      message: confirmMessage,
      payload: {
        entity_ids: [...selectedEntities],
        reset_all: selectedPreferences.length === 0,
        preference_keys: selectedPreferences.length > 0 ? [...selectedPreferences] : undefined
      }
    });
  };

  const handleExport = async () => {
    try {
      const entityId = localStorage.getItem('entityId');
      const response = await axios.get(`/api/admin/preferences/${entityId}/export?include_data=true`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { 
        type: 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `complete_preferences_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t('preferencesExportedWithData'));
    } catch (error) {
      console.error('Error exporting preferences:', error);
      toast.error(t('errorExportingPreferences'));
    }
  };

  const handleImport = async () => {
    if (!importData) {
      toast.error(t('pleaseSelectFileToImport'));
      return;
    }

    try {
      setLoading(true);
      
      const entityId = localStorage.getItem('entityId');
      const response = await axios.post(`/api/admin/preferences/${entityId}/import`, {
        preference_definitions: importData.preference_definitions,
        overwrite_existing: true
      });
      
      if (response.data.success) {
        toast.success(t('preferencesImportSuccess', {
          imported: response.data.imported,
          skipped: response.data.skipped
        }));
        
        if (response.data.errors && response.data.errors.length > 0) {
          console.warn('Import errors:', response.data.errors);
          toast.warning(t('someImportErrors'));
        }
        
        setImportData(null);
        // Refresh preferences list
        fetchData();
      }
      
    } catch (error) {
      console.error('Error importing preferences:', error);
      toast.error(error.response?.data?.error || t('errorImportingPreferences'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.preference_definitions && Array.isArray(data.preference_definitions)) {
          setImportData(data);
          toast.success(t('fileLoadedSuccessfully', { 
            count: data.preference_definitions.length 
          }));
        } else {
          toast.error(t('invalidFileFormat'));
        }
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error(t('errorParsingFile'));
      }
    };
    reader.readAsText(file);
  };

  const operations = [
    {
      id: 'reset-users',
      name: t('resetUserPreferences'),
      icon: Users,
      description: t('resetSelectedUsersToDefaults')
    },
    {
      id: 'reset-entities',
      name: t('resetEntitySettings'),
      icon: Building,
      description: t('resetSelectedEntitiesToDefaults')
    },
    {
      id: 'import-export',
      name: t('importExportConfigurations'),
      icon: RefreshCw,
      description: t('importOrExportPreferenceConfigurations')
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          <RotateCcw className="inline w-5 h-5 mr-2" />
          {t('bulkOperations')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t('performBulkPreferenceOperations')}
        </p>
      </div>

      {/* Operation Selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {operations.map((operation) => {
          const Icon = operation.icon;
          return (
            <button
              key={operation.id}
              onClick={() => setActiveOperation(operation.id)}
              className={`p-4 border rounded-lg text-left transition-colors ${
                activeOperation === operation.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 hover:border-gray-400 dark:border-gray-600'
              }`}
            >
              <Icon className={`w-6 h-6 mb-2 ${
                activeOperation === operation.id ? 'text-blue-600' : 'text-gray-400'
              }`} />
              <h4 className="font-medium text-gray-900 dark:text-white">
                {operation.name}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {operation.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Operation Content */}
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-6">
        {activeOperation === 'reset-users' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-medium">{t('warningResetUserPreferences')}</span>
            </div>

            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('selectUsers')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto border rounded p-3">
                {users.map((user) => (
                  <label key={user.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{user.first_name} {user.last_name} ({user.username})</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('selectedUsersCount', { count: selectedUsers.length })}
              </p>
            </div>

            {/* Preference Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('selectSpecificPreferences')} ({t('optional')})
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded p-3">
                {availablePreferences
                  .filter(pref => pref.preference_type === 'user_global')
                  .map((pref) => (
                    <label key={pref.preference_key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedPreferences.includes(pref.preference_key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPreferences([...selectedPreferences, pref.preference_key]);
                          } else {
                            setSelectedPreferences(selectedPreferences.filter(key => key !== pref.preference_key));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{pref.preference_key}</span>
                    </label>
                  ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPreferences.length === 0 
                  ? t('allPreferencesWillBeReset')
                  : t('selectedPreferencesCount', { count: selectedPreferences.length })
                }
              </p>
            </div>

            <button
              onClick={handleResetUsers}
              disabled={loading || selectedUsers.length === 0}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {t('resetUserPreferences')}
            </button>
          </div>
        )}

        {activeOperation === 'reset-entities' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm font-medium">{t('warningResetEntitySettings')}</span>
            </div>

            {/* Entity Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('selectEntities')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded p-3">
                {entities.map((entity) => (
                  <label key={entity.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedEntities.includes(entity.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEntities([...selectedEntities, entity.id]);
                        } else {
                          setSelectedEntities(selectedEntities.filter(id => id !== entity.id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{entity.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('selectedEntitiesCount', { count: selectedEntities.length })}
              </p>
            </div>

            {/* Setting Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('selectSpecificSettings')} ({t('optional')})
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded p-3">
                {availablePreferences
                  .filter(pref => pref.preference_type === 'entity')
                  .map((pref) => (
                    <label key={pref.preference_key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedPreferences.includes(pref.preference_key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPreferences([...selectedPreferences, pref.preference_key]);
                          } else {
                            setSelectedPreferences(selectedPreferences.filter(key => key !== pref.preference_key));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm">{pref.preference_key}</span>
                    </label>
                  ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPreferences.length === 0 
                  ? t('allSettingsWillBeReset')
                  : t('selectedSettingsCount', { count: selectedPreferences.length })
                }
              </p>
            </div>

            <button
              onClick={handleResetEntities}
              disabled={loading || selectedEntities.length === 0}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              {t('resetEntitySettings')}
            </button>
          </div>
        )}

        {activeOperation === 'import-export' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Export Section */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  <Download className="inline w-4 h-4 mr-2" />
                  {t('exportConfiguration')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('exportPreferencesAndData')}
                </p>
                <button
                  onClick={handleExport}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('exportAll')}
                </button>
              </div>

              {/* Import Section */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  <Upload className="inline w-4 h-4 mr-2" />
                  {t('importConfiguration')}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('importPreferenceDefinitions')}
                </p>
                
                <div className="space-y-3">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  
                  {importData && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3">
                      <div className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-800 dark:text-green-200">
                          {t('fileLoaded', { count: importData.preference_definitions.length })}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={handleImport}
                    disabled={!importData || loading}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {t('importConfiguration')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialogShell
        isOpen={!!pendingConfirmation}
        onClose={() => !loading && setPendingConfirmation(null)}
        onConfirm={confirmPendingOperation}
        title={pendingConfirmation?.title || t('confirm')}
        confirmLabel={pendingConfirmation?.confirmLabel || t('confirm')}
        confirmVariant="danger"
        confirmIcon={RotateCcw}
        isLoading={loading}
        isDisabled={loading}
        showIcon={true}
        iconVariant="warning"
        closeOnBackdropClick={!loading}
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {pendingConfirmation?.message}
        </p>
      </ConfirmDialogShell>
    </div>
  );
};

export default BulkOperations;
